import React, { useCallback, useEffect, useState } from 'react';
import { DatePicker, Modal, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { FaHandshakeAngle } from 'react-icons/fa6';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { endVisit, fetchDoctorDailyVisits } from '../services/visitService';
import { hasRole } from '../services/authServices';
import {
    DataTable,
    FilterPills,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    VisitStatusBadge,
} from '../components/ui';

const PAGE_SIZE = 20;
const DAILY_STATUSES = ['queue', 'examined', 'closed'];

function visitPatient(record) {
    return record?.patient ?? {};
}

function visitPatientId(record) {
    return record?.patient?.id ?? record?.patient_id;
}

function visitServiceName(record) {
    return record?.service?.name
        ?? record?.services?.[0]?.name
        ?? record?.service_name
        ?? null;
}

function visitTime(record) {
    return record?.time
        ?? record?.time_at
        ?? record?.start_time
        ?? record?.queue?.time
        ?? null;
}

function visitQueueLabel(record) {
    const queue = record?.queue;
    if (queue == null) return record?.queue_number ?? null;
    return queue.number ?? queue.name ?? queue.id ?? null;
}

function formatPhone(phone) {
    if (!phone) return null;
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length === 12) {
        return `+998 (${digits.slice(3, 5)}) ${digits.slice(5, 8)}-${digits.slice(8, 10)}-${digits.slice(10, 12)}`;
    }
    return String(phone);
}

export default function DoctorVisits() {
    const { t } = useTranslation();
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateAt, setDateAt] = useState(() => dayjs());
    const [statusFilter, setStatusFilter] = useState('all');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: PAGE_SIZE,
        total: 0,
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [userData, setUserData] = useState(null);
    const isDoctor = hasRole('doctor');

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                setUserData(JSON.parse(storedUser));
            } catch {
                setUserData(null);
            }
        }
    }, []);

    const fetchData = useCallback(async () => {
        if (!hasRole('doctor')) {
            setVisits([]);
            setPagination((prev) => ({ ...prev, total: 0 }));
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const statuses = statusFilter === 'all' ? undefined : [statusFilter];
            const result = await fetchDoctorDailyVisits({
                date_at: dateAt.format('YYYY-MM-DD'),
                statuses,
                page: pagination.current,
                per_page: pagination.pageSize,
            });
            setVisits(result.visits);
            setPagination((prev) => ({
                ...prev,
                current: result.pagination.current,
                pageSize: result.pagination.pageSize,
                total: result.pagination.total,
            }));
        } catch (error) {
            setVisits([]);
            const code = error?.response?.status;
            if (code === 403) {
                toast.error(t('doctorVisits.forbidden'));
            } else if (code !== 401) {
                toast.error(t('doctorVisits.loadError'));
            }
        } finally {
            setLoading(false);
        }
    }, [dateAt, pagination.current, pagination.pageSize, statusFilter, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openModal = (id) => {
        setSelectedRecordId(id);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedRecordId(null);
    };

    const handleEndVisit = async (visitId) => {
        try {
            await endVisit(visitId);
            await fetchData();
            closeModal();
            toast.success(t('doctorVisits.ended'));
        } catch (error) {
            console.error('Error ending visit:', error);
            toast.error(t('doctorVisits.endError'));
        }
    };

    const handleTableChange = (pager) => {
        setPagination((prev) => ({
            ...prev,
            current: pager.current,
            pageSize: pager.pageSize || prev.pageSize,
        }));
    };

    const patientLink = (record) => {
        const patientId = visitPatientId(record);
        if (!patientId) return '/doctor/visits';
        return record.id ? `/patient/${patientId}/visit/${record.id}` : `/patient/${patientId}`;
    };

    const columns = [
        {
            title: t('patients.colId'),
            key: 'id',
            width: 72,
            render: (_, record) => (
                <span className="text-xs tabular-nums text-slate-500">{record.id}</span>
            ),
        },
        {
            title: t('doctorVisits.patientName'),
            key: 'patient',
            render: (_, record) => {
                const patient = visitPatient(record);
                const name = patient.name ?? record.patient_name ?? t('doctorVisits.unknownPatient');
                const code = patient.code ?? record.patient_code;
                const phone = formatPhone(patient.phone ?? record.patient_phone);

                return (
                    <div className="min-w-[160px]">
                        <Link
                            className="block truncate text-sm font-medium text-[#1D2C7B] hover:underline"
                            to={patientLink(record)}
                        >
                            {name}
                        </Link>
                        <span className="mt-0.5 block text-xs text-slate-500">
                            {code ? `${t('common.code')}: ${code}` : null}
                            {code && phone ? ' · ' : null}
                            {phone}
                        </span>
                    </div>
                );
            },
        },
        {
            title: t('doctorVisits.service'),
            key: 'service',
            render: (_, record) => (
                <span className="text-sm text-slate-700">
                    {visitServiceName(record) || t('common.dash')}
                </span>
            ),
        },
        {
            title: t('doctorVisits.time'),
            key: 'time',
            width: 110,
            render: (_, record) => (
                <span className="text-sm tabular-nums text-slate-600">
                    {visitTime(record) || t('common.dash')}
                </span>
            ),
        },
        {
            title: t('doctorVisits.queueNo'),
            key: 'queue',
            width: 100,
            render: (_, record) => (
                <span className="text-sm tabular-nums text-slate-600">
                    {visitQueueLabel(record) ?? t('common.dash')}
                </span>
            ),
        },
        {
            title: t('common.status'),
            dataIndex: 'status',
            key: 'status',
            width: 160,
            render: (status) => <VisitStatusBadge status={status} />,
        },
        {
            title: t('common.actions'),
            key: 'action',
            width: 88,
            fixed: 'right',
            render: (_, record) => {
                const canEnd = record.status === 'examined' || record.status === 'queue';

                return (
                    <div className="flex items-center gap-0.5">
                        <Tooltip title={t('doctorVisits.patientCard')}>
                            <Link
                                to={patientLink(record)}
                                className="inline-flex rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#1D2C7B]"
                            >
                                <DocumentTextIcon className="h-[18px] w-[18px]" />
                            </Link>
                        </Tooltip>
                        {canEnd && (
                            <Tooltip title={t('doctorVisits.endVisit')}>
                                <RowIconButton
                                    onClick={() => openModal(record.id)}
                                    aria-label={t('doctorVisits.endVisit')}
                                >
                                    <FaHandshakeAngle className="h-[18px] w-[18px]" />
                                </RowIconButton>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
    ];

    const renderMobileCard = (record) => {
        const patient = visitPatient(record);
        const name = patient.name ?? record.patient_name ?? t('doctorVisits.unknownPatient');
        const canEnd = record.status === 'examined' || record.status === 'queue';
        const service = visitServiceName(record);
        const time = visitTime(record);

        return (
            <div>
                <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                        <Link
                            to={patientLink(record)}
                            className="block truncate text-sm font-semibold text-[#1D2C7B]"
                        >
                            {name}
                        </Link>
                        <span className="mt-0.5 block text-xs text-slate-500">
                            {patient.code ? `${t('common.code')}: ${patient.code}` : null}
                            {patient.code && patient.phone ? ' · ' : null}
                            {formatPhone(patient.phone)}
                        </span>
                    </div>
                    {canEnd && (
                        <RowIconButton onClick={() => openModal(record.id)} aria-label={t('doctorVisits.endVisit')}>
                            <FaHandshakeAngle className="h-[18px] w-[18px]" />
                        </RowIconButton>
                    )}
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <VisitStatusBadge status={record.status} />
                    {time && <span className="text-xs tabular-nums text-slate-600">{time}</span>}
                    {service && <span className="text-xs text-slate-500">{service}</span>}
                </div>
            </div>
        );
    };

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('doctorVisits.title')}
                    count={pagination.total}
                    filters={
                        <>
                            {userData?.name && (
                                <span className="hidden text-xs text-slate-500 sm:inline">
                                    {userData.name}
                                </span>
                            )}
                            <DatePicker
                                size="small"
                                value={dateAt}
                                allowClear={false}
                                format="DD.MM.YYYY"
                                onChange={(value) => {
                                    if (!value) return;
                                    setDateAt(value);
                                    setPagination((prev) => ({ ...prev, current: 1 }));
                                }}
                                className="md:!w-[140px]"
                            />
                        </>
                    }
                />

                <FilterPills
                    value={statusFilter}
                    onChange={(value) => {
                        setStatusFilter(value);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                    }}
                    options={[
                        { value: 'all', label: t('doctorVisits.allStatuses') },
                        ...DAILY_STATUSES.map((status) => ({
                            value: status,
                            label: t(`status.${status}`),
                        })),
                    ]}
                />

                <DataTable
                    columns={columns}
                    dataSource={visits}
                    loading={loading}
                    locale={{ emptyText: isDoctor ? t('common.empty') : t('doctorVisits.forbidden') }}
                    mobileCard={renderMobileCard}
                    rowKey="id"
                    scrollX={860}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                        showTotal: (total, range) => (
                            <span className="text-xs text-slate-500">
                                {t('common.perPage', { from: range[0], to: range[1], total })}
                            </span>
                        ),
                    }}
                    onChange={handleTableChange}
                />
            </PageCard>

            <Modal
                title={t('doctorVisits.confirm')}
                open={isModalOpen}
                onCancel={closeModal}
                onOk={() => handleEndVisit(selectedRecordId)}
                okText={t('doctorVisits.yesEnd')}
                cancelText={t('common.cancel')}
                okButtonProps={{ danger: true }}
                centered
                width="100%"
                style={{ maxWidth: 400 }}
            >
                <p className="m-0 text-sm text-slate-600">
                    {t('doctorVisits.confirmEnd')}
                </p>
            </Modal>
        </PageShell>
    );
}
